import React, { useEffect, useMemo, useState } from 'react';
import { GitBranch, Plus, Save, Users, Workflow, Cpu, Cloud, ChevronDown, Eye } from 'lucide-react';
import Skeleton from '../../common/Skeleton';
import Modal from '../../common/Modal';
import Button from '../../common/Button';
import Toast, { ToastMessage } from '../../common/Toast';
import {
    capaRootCausesService, CapaRootCause, RCABucket, RootCauseCreate,
} from '../../../services/capaRootCausesService';
import RCAIdentifierPicker from './RCAIdentifierPicker';
import CauseAnalysisModal from './CauseAnalysisModal';
import extractApiError from '../../../utils/apiError';
import { useFieldValidation } from '../../../validation/useFieldValidation';
import { ValidatedTextarea } from '../../../validation/ValidatedFields';
import { useTreeCycleGuard } from '../../../validation/useTreeCycleGuard';
import { User } from '../../../services/userService';

const BUCKETS: { bucket: RCABucket; icon: React.ElementType }[] = [
    { bucket: 'People',       icon: Users },
    { bucket: 'Process',      icon: Workflow },
    { bucket: 'Technology',   icon: Cpu },
    { bucket: 'Environment',  icon: Cloud },
];

interface Props {
    investigationId: number;
    selectedImpactId: number | null;
    selectedCauseId: number | null;
    onSelectCause: (id: number | null) => void;
    users: User[];
    onChanged: () => void;
    isLocked?: boolean;
}

const RCAColumn: React.FC<Props> = ({ investigationId, selectedImpactId, selectedCauseId, onSelectCause, users, onChanged, isLocked }) => {
    const [causes, setCauses] = useState<CapaRootCause[]>([]);
    const [loading, setLoading] = useState(false);
    const [expandedBuckets, setExpandedBuckets] = useState<Record<RCABucket, boolean>>({
        People: true, Process: true, Technology: true, Environment: true,
    });
    // Add-cause modal (used for root causes only)
    const [showAdd, setShowAdd] = useState<{ bucket: RCABucket | ''; parentId: number | null } | null>(null);
    // form state — bucket may be '' until user picks one (root-cause add path)
    const [form, setForm] = useState<Omit<RootCauseCreate, 'bucket'> & { bucket: RCABucket | '' }>(
        { bucket: 'People', cause_description: '', impact_id: 0 }
    );
    const [submitting, setSubmitting] = useState(false);
    // Unified analysis modal: which cause is open
    const [analysisCause, setAnalysisCause] = useState<CapaRootCause | null>(null);
    const [toast, setToast] = useState<ToastMessage | null>(null);
    const fieldVh = useFieldValidation();

    // Cycle guard: when creating/editing a cause, forbid selecting a cause as parent if it would create a cycle
    const { forbiddenIds: cycleGuardForbiddenIds } = useTreeCycleGuard(
        causes,
        null, // Always creating new (not editing), so no self to guard against
        { idKey: 'id', parentKey: 'prev_record_id' }
    );

    const loadCauses = async () => {
        if (!selectedImpactId) { setCauses([]); return; }
        try {
            setLoading(true);
            setCauses(await capaRootCausesService.list(investigationId, selectedImpactId));
        } catch (e: any) {
            setToast({ type: 'error', message: extractApiError(e, 'Failed to load causes') });
        } finally { setLoading(false); }
    };

    useEffect(() => { loadCauses(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [investigationId, selectedImpactId]);
    useEffect(() => { setAnalysisCause(null); }, [selectedImpactId]);
    useEffect(() => {
        if (!showAdd) return;
        fieldVh.reset();
    }, [showAdd, fieldVh]);

    const tree = useMemo(() => {
        const byParent = new Map<number | null, CapaRootCause[]>();
        for (const c of causes) {
            const key = c.prev_record_id ?? null;
            if (!byParent.has(key)) byParent.set(key, []);
            byParent.get(key)!.push(c);
        }
        return byParent;
    }, [causes]);

    const toggleBucket = (b: RCABucket) => setExpandedBuckets(prev => ({ ...prev, [b]: !prev[b] }));

    const openAdd = (bucket: RCABucket | '', parentId: number | null) => {
        if (isLocked) return;
        if (!selectedImpactId) return;
        setForm({ bucket, cause_description: '', prev_record_id: parentId ?? undefined, impact_id: selectedImpactId });
        setShowAdd({ bucket, parentId });
    };

    // Check if the current form has a cycle-guard violation
    const hasCycleError = form.prev_record_id !== undefined && form.prev_record_id !== null && cycleGuardForbiddenIds.has(form.prev_record_id);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        // Cycle guard check
        if (hasCycleError) {
            return;
        }

        // Validate required fields
        if (!form.bucket) {
            fieldVh.setError('bucket', 'Bucket is required');
            return;
        }
        fieldVh.clearError('bucket');

        if (!form.impact_id) {
            fieldVh.setError('impact_id', 'Select an impact first');
            return;
        }
        fieldVh.clearError('impact_id');

        // Validate code 110 field (required)
        const valid = fieldVh.validateAll([
            { name: 'cause_description', code: 110, value: form.cause_description, required: true },
        ]);
        if (!valid) return;

        try {
            setSubmitting(true);
            // After guard check above, bucket is guaranteed non-empty
            const payload = { ...form, bucket: form.bucket as RCABucket };
            await capaRootCausesService.create(investigationId, payload);
            setToast({ type: 'success', message: 'Cause added.' });
            setShowAdd(null);
            await loadCauses();
            onChanged();
        } catch (e: any) {
            setToast({ type: 'error', message: extractApiError(e, 'Failed to add cause') });
        } finally { setSubmitting(false); }
    };

    // Render a ROOT cause card in the main column (summary-only; opens the unified modal)
    const renderRootCard = (c: CapaRootCause) => {
        const isSelected = selectedCauseId === c.id;
        return (
            <div
                key={c.id}
                className={`collapse-card${isSelected ? ' collapse-card--selected' : ''}`}
                // Always select on click (do NOT toggle off when already selected):
                // re-clicking the same cause must keep its Remedial Actions visible.
                // Deselection happens by picking a different cause or switching impact.
                onClick={() => onSelectCause(c.id)}
                data-testid={`BTN_rca_cause_${c.id}_open`}
            >
                <div className="collapse-card__row">
                    <span className="collapse-card__title">{c.cause_description}</span>
                    <button
                        type="button"
                        className="btn-primary-outline-none"
                        onClick={(e) => { e.stopPropagation(); onSelectCause(c.id); setAnalysisCause(c); }}
                        title="View / analyze cause"
                        data-testid={`BTN_rca_cause_${c.id}_view`}
                    >
                        <Eye size={12} />
                    </button>
                </div>
                <div className="collapse-card__badges">
                    <span className="status-badge sm neutral" data-testid={`TXT_capa_root_cause_row_${c.id}_code`}>{c.code || '—'}</span>
                    {c.linked_item_label && <span className="status-badge sm neutral">{c.linked_item_label}</span>}
                    {(c.task_count ?? 0) > 0 && (
                        <span className="status-badge sm info">{c.task_count} action{c.task_count === 1 ? '' : 's'}</span>
                    )}
                </div>
            </div>
        );
    };

    return (
        <div className="capa-investigation-col">
            <div className="capa-investigation-col-header">
                <GitBranch size={14} />
                Root Cause Analysis
                <span className="capa-investigation-col-header-spacer" />
                {!isLocked && selectedImpactId && (
                    <button
                        type="button"
                        className="btn-icon-only"
                        onClick={() => openAdd('', null)}
                        data-tooltip="Add root cause"
                        data-tooltip-pos="left"
                        data-testid="BTN_rca_add_root"
                    >
                        <Plus size={16} />
                    </button>
                )}
            </div>
            <div className="capa-investigation-col-body" style={{ padding: 12 }}>
                {!selectedImpactId && (
                    <div style={{ padding: 24, textAlign: 'center', color: '#9ca3af', fontSize: '0.85rem' }}>
                        Select an impact on the left to add or view its causes.
                    </div>
                )}

                {selectedImpactId && loading && (
                    <div className="collapse-section-stack">
                        {BUCKETS.map(({ bucket }) => (
                            <div key={bucket} className="collapse-section collapse-section--l2 cs-open">
                                <div className="collapse-section__header">
                                    <span className="collapse-section__toggle"><ChevronDown size={12} /></span>
                                    <Skeleton.Box width={13} height={13} radius={3} />
                                    <Skeleton.Box width={72} height={12} radius={4} style={{ marginLeft: 4 }} />
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {selectedImpactId && !loading && (() => {
                    const nonEmpty = BUCKETS
                        .map(b => ({ ...b, roots: (tree.get(null) ?? []).filter(c => c.bucket === b.bucket) }))
                        .filter(b => b.roots.length > 0);

                    if (nonEmpty.length === 0) {
                        return (
                            <div style={{ padding: 24, textAlign: 'center', color: '#9ca3af', fontSize: '0.85rem' }} data-testid="MSG_rca_empty">
                                No causes identified yet — click + to add one.
                            </div>
                        );
                    }

                    return (
                        <div className="collapse-section-stack">
                            {nonEmpty.map(({ bucket, icon: Icon, roots }) => {
                                const isOpen = expandedBuckets[bucket];
                                return (
                                    <div key={bucket} className={`collapse-section collapse-section--l2 ${isOpen ? 'cs-open' : 'cs-closed'}`}>
                                        <div className="collapse-section__header" onClick={() => toggleBucket(bucket)} data-testid={`BTN_rca_bucket_${bucket}_toggle`}>
                                            <span className="collapse-section__toggle">
                                                <ChevronDown size={12} />
                                            </span>
                                            <Icon size={13} />
                                            <span className="collapse-section__name">{bucket}</span>
                                            <div className="collapse-section__meta">
                                                <span className="status-badge sm neutral">{roots.length}</span>
                                            </div>
                                        </div>
                                        {isOpen && (
                                            <div className="collapse-section__body">
                                                {roots.map(c => renderRootCard(c))}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    );
                })()}
            </div>

            {/* Add-cause modal — creates ROOT causes */}
            <Modal isOpen={!!showAdd} onClose={() => setShowAdd(null)} size="md">
                <Modal.Header>
                    {form.bucket ? `Create ${form.bucket} Cause` : 'Create Root Cause'}
                </Modal.Header>
                <Modal.Body>
                    <form id="add-cause-form" onSubmit={handleSubmit} className="flex flex-col gap-y-4" data-testid="MOD_rca_add_cause_form">
                        {hasCycleError && (
                            <div className="text-sm text-red-600 bg-red-50 p-3 rounded border border-red-200" data-testid="CHK_rca_cycle_error">
                                Cannot create cycle: parent is descendant of this cause
                            </div>
                        )}
                        {!form.prev_record_id && (
                            <div data-testid="GRP_rca_category_picker">
                                <label className="form-label">Category <span style={{ color: 'var(--danger, #dc2626)' }}>*</span></label>
                                <div className="capa-rca-category-grid">
                                    {BUCKETS.map(({ bucket, icon: Icon }) => (
                                        <button
                                            key={bucket}
                                            type="button"
                                            className={`capa-rca-category-option${form.bucket === bucket ? ' capa-rca-category-option--active' : ''}`}
                                            onClick={() => { setForm(f => ({ ...f, bucket })); fieldVh.clearError('bucket'); }}
                                            data-testid={`BTN_rca_category_${bucket}`}
                                        >
                                            <Icon size={14} />
                                            <span>{bucket}</span>
                                        </button>
                                    ))}
                                </div>
                                {fieldVh.errors.bucket && (
                                    <span className="fg__error" role="alert" data-testid="ERR_rca_category">{fieldVh.errors.bucket}</span>
                                )}
                            </div>
                        )}
                        <ValidatedTextarea
                            vh={fieldVh}
                            name="cause_description"
                            code={110}
                            required={true}
                            label="Cause Description"
                            value={form.cause_description}
                            onChange={e => setForm(f => ({ ...f, cause_description: e.target.value }))}
                            placeholder="What is the cause?"
                            rows={3}
                            data-testid="INP_rca_cause_description"
                        />
                        {form.bucket && (
                            <RCAIdentifierPicker
                                bucket={form.bucket}
                                itemTypeId={form.item_type_id ?? null}
                                itemRecordId={form.item_record_id ?? null}
                                onChange={(tid, rid) => setForm(f => ({ ...f, item_type_id: tid, item_record_id: rid }))}
                            />
                        )}
                    </form>
                </Modal.Body>
                <Modal.Footer>
                    <Button type="submit" form="add-cause-form" variant="fill" icon={Save} disabled={submitting || hasCycleError} data-testid="BTN_rca_cause_submit">
                        {submitting ? 'Submitting...' : 'Submit'}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Unified Cause Analysis Modal — 3-step stepper (Reason / Identify / Remediate) */}
            <CauseAnalysisModal
                isOpen={!!analysisCause}
                investigationId={investigationId}
                cause={analysisCause}
                allCauses={causes}
                users={users}
                readOnly={!!isLocked}
                onClose={() => setAnalysisCause(null)}
                onChanged={() => { loadCauses(); onChanged(); }}
            />

            <Toast toast={toast} onClose={() => setToast(null)} />
        </div>
    );
};

export default RCAColumn;
